import { Card, Flex } from "antd"
import React, { useEffect, useMemo } from "react"

import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector"
import { WidgetsDashboard } from "../../DashboardPage/components/Portfolio/WidgetsDashboard.tsx"
import { BranchComparisonTable } from "../components/BranchComparisonTable.tsx"
import { BranchSetupFlow } from "../components/BranchSetupFlow.tsx"
import { PageBranchFilter } from "../components/PageBranchFilter.tsx"
import {
  fetchCompanyDashboard,
  selectCompanyDashboard,
  selectFetchingCompanyDashboard,
  selectPageFilter
} from "../redux/branches.slice.ts"

// A dedicated `UIPreferences` key (via the generic per-surface `preferences`
// bag — see `useDashboardLayout`) so editing Company HQ's dashboard layout
// can never read or overwrite a single-branch workspace's own
// `dashboard_config`, and vice versa.
const HQ_DASHBOARD_PREFERENCE_KEY = "hq_dashboard_config"

const money = (value: string) => Number(value)

/**
 * The Company Overview's "Dashboard" module.
 *
 * Reuses the exact same customizable widget system the single-branch Home
 * page uses (`WidgetsDashboard` — Add tile, Edit Layout, drag/reorder,
 * saved views, all unmodified) rather than a separate, incompatible
 * editor. The only two things specific to this mount: a different
 * preference key (so the two layouts never collide) and this page's own
 * branch filter, threaded in as a fixed `teamScope` that narrows every
 * tile's underlying measure query — the same additive, narrowing-only
 * `GlobalFilters.teams` dimension proven safe for Invoices.
 *
 * Branch comparison and the cross-branch "needs attention" summary are
 * NOT yet tiles in the existing Add Tile catalog (no registered widget
 * type for either), so they render as a fixed section below the
 * customizable area — clearly labelled as such rather than pretending to
 * be part of the tile system. Sourced from
 * `apps.user.services.company_dashboard_service`, itself a thin
 * re-aggregation of the exact functions/predicates the single-branch app
 * already trusts — nothing here computes a total by hand.
 */
export const CompanyDashboardPage = () => {
  const dispatch = useAppDispatch()
  const scope = useAppSelector(selectPageFilter("dashboard"))
  const dashboard = useAppSelector(selectCompanyDashboard)
  const fetching = useAppSelector(selectFetchingCompanyDashboard)

  const teamScope = useMemo(
    () => (scope.type === "subset" ? scope.teamIds : undefined),
    [scope]
  )

  useEffect(() => {
    generateDocumentTitle("Company HQ")
  }, [])

  useEffect(() => {
    dispatch(
      fetchCompanyDashboard(scope.type === "all" ? { all: true } : { teams: scope.teamIds })
    )
  }, [dispatch, scope])

  const attention =
    dashboard?.branches.filter((b) => b.delayed_projects > 0 || b.overdue_count > 0) ?? []

  return (
    <Flex vertical gap="middle" className="p-4">
      <BranchSetupFlow />
      <Flex justify="space-between" align="center">
        <h2 className="text-lg font-semibold m-0">Company HQ</h2>
        <PageBranchFilter page="dashboard" />
      </Flex>

      <WidgetsDashboard preferenceKey={HQ_DASHBOARD_PREFERENCE_KEY} teamScope={teamScope} />

      <Card
        size="small"
        title="Needs attention across branches"
        loading={fetching && !dashboard}
      >
        {dashboard &&
          (attention.length === 0 ? (
            <span className="text-gray-400">Nothing needs attention right now.</span>
          ) : (
            <ul className="list-disc pl-4">
              {attention.map((b) => (
                <li key={b.id}>
                  {b.name}
                  {b.delayed_projects > 0 && ` — ${b.delayed_projects} delayed project(s)`}
                  {b.overdue_count > 0 &&
                    ` — ${b.overdue_count} overdue invoice(s), $${money(b.overdue).toLocaleString()}`}
                </li>
              ))}
            </ul>
          ))}
      </Card>

      <Card size="small" title="Branch comparison" loading={fetching && !dashboard}>
        {dashboard && <BranchComparisonTable rows={dashboard.branches} />}
      </Card>
    </Flex>
  )
}

export default CompanyDashboardPage
